import random from "lodash/random";
import { stackReportUnitTypeModes } from "common/enums";
import { buildTree } from "common/helpers";
import { SelectOption } from "components/common/Select";
import { AreaTree, AreasExportItem, AreasLoadItem, InjectorStackItem, StackReportFuelTypeOption, StackReportUnitTypeOption, StackReportDashboardResponse } from "store/stackReportDashboard/types";

interface ScenarioLookupRow {
    scenario: string;
}

interface UnitTypeLookupRow {
    UnitType: string;
}

interface FuelTypeLookupRow {
    PrimaryFuelType: string;
}

interface AreaLookupRow {
    Area: string;
    ParentArea: string | null;
}

interface InjectorStackRow {
    avg_of_max: string;
    avg_of_p: string;
    oprate_avg: string;
    inj: string;
    scenario: string;
    unittype: string;
    fueltype: string;
    energyarea: string;
}

interface AreaLoadRow {
    AvgAreasLoad: string;
    MaxAreasLoad: string;
    area: string;
    scenario: string;
}

interface AreaExportRow {
    avgAreasExports: string;
    area: string;
    scenario: string;
}

export interface StackReportDashboardItem {
    scenarios?: SelectOption[];
    unitTypes?: StackReportUnitTypeOption[];
    fuelTypes?: StackReportFuelTypeOption[];
    areas?: AreaTree;
    injectorStack?: InjectorStackItem[];
    areasLoad?: AreasLoadItem[];
    areasExport?: AreasExportItem[];
}

export const mapDashboardItem = (i: StackReportDashboardResponse): StackReportDashboardItem => {
    const result: StackReportDashboardItem = {};

    if (i.scenarios !== undefined)
        result.scenarios = (JSON.parse(i.scenarios) as ScenarioLookupRow[]).map((o) => ({
            key: random(Number.MAX_SAFE_INTEGER, false),
            displayName: o.scenario,
        }));

    if (i.unitTypes !== undefined)
        result.unitTypes = (JSON.parse(i.unitTypes) as UnitTypeLookupRow[]).map((o, idx) =>
            ({ key: idx, idx, displayName: o.UnitType, mode: stackReportUnitTypeModes.max }));
    if (i.fuelTypes !== undefined)
        result.fuelTypes = (JSON.parse(i.fuelTypes) as FuelTypeLookupRow[]).map((o, idx) => ({ key: idx, idx, displayName: o.PrimaryFuelType }));

    if (i.areas !== undefined) {
        const areas = (JSON.parse(i.areas) as AreaLookupRow[]).map((o) => ({ key: o.Area, displayName: o.Area, parent: o.ParentArea }));
        result.areas = buildTree(areas);
    }

    if (i.injector_stack !== undefined) {
        result.injectorStack = (JSON.parse(i.injector_stack) as InjectorStackRow[]).map((o) => ({
            avgOfMax: parseFloat(o.avg_of_max),
            avgOfP: parseFloat(o.avg_of_p),
            opRateAvg: parseFloat(o.oprate_avg),
            injector: o.inj,
            scenario: o.scenario,
            unitType: o.unittype,
            fuelType: o.fueltype,
            area: o.energyarea,
        }));
    }
    if (i.area_load !== undefined) {
        result.areasLoad = (JSON.parse(i.area_load) as AreaLoadRow[]).map((o) => ({
            avgAreaLoad: parseFloat(o.AvgAreasLoad),
            maxAreaLoad: parseFloat(o.MaxAreasLoad),
            area: o.area,
            scenario: o.scenario,
        }));
    }
    if (i.area_exports !== undefined) {
        result.areasExport = (JSON.parse(i.area_exports) as AreaExportRow[]).map((o) => ({
            avgAreaExport: parseFloat(o.avgAreasExports),
            area: o.area,
            scenario: o.scenario,
        }));
    }

    return result;
};

// Used insted of rendomColor. Genarete hsl random color point in the chart.
const GOLDEN_ANGLE = 137.508;
export const generateColor = (index: number): string => `hsl(${Math.round((index * GOLDEN_ANGLE) % 360)}, 65%, 55%)`;

export function findParents(tree: AreaTree, values: string[]): string[] {
    const nodeKeys = new Set(values);
    const parentKeys: string[] = [];
    const keys = [...tree].filter(([, node]) => ["", null].includes(node.parent)).map(([key]) => key);

    while (keys.length > 0) {
        const nodeKey = keys.shift();

        if (nodeKey === undefined) {
            break;
        }

        if (nodeKeys.has(nodeKey)) {
            parentKeys.push(nodeKey);
            continue;
        }

        const node = tree.get(nodeKey);

        if (node) {
            keys.push(...node.children);
        }
    }

    return parentKeys;
}

export function array2Csv<TItem extends object>(data: TItem[], header = true): string {
    if (data == null || !data.length) {
        return "";
    }

    let result = "";
    const keys = Object.keys(data[0]) as (keyof TItem)[];

    if (header) {
        result += keys.join(",");
        result += "\n";
    }

    for (const item of data) {
        result += keys.map(key => item[key]).join(",");
        result += "\n";
    }

    return result;
}